import { NetStandardService } from './../../services/net-standard-service';
import { CustomerNetTypeService } from 'services/customer-net-type-service';
import { NettingService } from 'services/netting-service';
import { DialogCloseResult, DialogService } from 'aurelia-dialog';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { autoinject, bindable, LogManager } from 'aurelia-framework';
import { Router } from 'aurelia-router';
import { Prompt } from 'elements/prompt';
import { Models } from 'models/core';
import { DesignTypeService } from 'services/design-type-service';
import { DimensionClassService } from 'services/dimension-class-service';
import { ErrorService } from 'services/error-service';
import { NetDimensionService } from 'services/net-dimension-service';
import { NetIntentService } from 'services/net-intent-service';
import { NetService } from 'services/net-service';
import { NetShapeService } from 'services/net-shape-service';
import { NetTypeService } from 'services/net-type-service';
import { NettingTypeService } from 'services/netting-type-service';
import { ToastService } from 'services/toast-service';
import { Utility } from '../../utility';
import { Logger } from 'aurelia-logging';
import { dimensionClass } from 'models/NetDimension';
import { AnalysisLookupDialog } from 'components/dialogs/analysis-lookup/analysis-lookup-dialog';
import { ValidationController, Validator } from 'aurelia-validation';
import { StandardValidationBuilder } from 'lib/validation';
import { I18N } from 'aurelia-i18n';
import { AsyncErrorHandler } from 'lib/ui';
import { FilterDeletedAndInactive } from 'lib/helpers/filters';

const logger: Logger = LogManager.getLogger('form-dimension');

enum NetShapeIds {
  Conednet = 3,
  Straightsides = 4,
  Spaghetti = 7,
  Cylinder = 8,
  Rectangular = 9,
}

@autoinject
export class FormDimension {
  @bindable
  private netId: number;

  @bindable
  private net: Models.Net;

  @bindable
  private netdimensionCalculated: Models.NetDimension;

  @bindable
  protected updateFunction: () => void;

  @bindable
  protected cancelFunction: () => void;

  @bindable
  protected calcFunction: () => void;

  @bindable
  protected dimensionclassType: 0 | 1 | 2;

  @bindable
  private locked: boolean = false;

  private dimensionclassTypeUsedNet: number;

  private originalObject: Models.Net;
  private nextTabIndex: number = null;
  private currentNetShape: Models.NetShape;

  protected offerDrawingsVisible: boolean = false;
  protected otherDrawingsVisible: boolean = false;
  protected fileUploaded: boolean = false;

  private refreshDimensionsSubscription: Subscription;

  protected is2021Standard = false;
  protected classType = '';

  protected isNS9415 = false;

  protected nettings: Models.Netting[];
  protected nettingtypes: Models.NettingType[];
  protected dimensionclasses: Models.DimensionClass[];

  protected netTypes: Models.NetType[] = [];
  protected netTypesFiltered: Models.NetType[] = [];

  protected designTypes: Models.DesignType[] = [];

  protected netShapes: Models.NetShape[] = [];
  protected warnings = {
    weight: {
      show: false,
      message: '',
    },
  };

  protected inInitialPhase = true;

  constructor(
    private designTypeService: DesignTypeService,
    private dialogService: DialogService,
    private dimensionClassService: DimensionClassService,
    private customerNetTypeService: CustomerNetTypeService,
    private errorService: ErrorService,
    private eventAggregator: EventAggregator,
    private netDimensionService: NetDimensionService,
    private netIntentService: NetIntentService,
    private netStandardService: NetStandardService,
    private netService: NetService,
    private netShapeService: NetShapeService,
    private nettingTypeService: NettingTypeService,
    private netTypeService: NetTypeService,
    private router: Router,
    private toastService: ToastService,
    private nettingService: NettingService,
    private utility: Utility,
    private validationController: ValidationController,
    private validator: Validator,
    private validationBuilder: StandardValidationBuilder,
    private t: I18N
  ) {
    this.warnings.weight.message = this.t.tr('validation.valueShouldNormalyBeInRangeOfXandYUnit', {
      x: '100',
      y: '20.000',
      unit: 'kg',
    });

    this.validationController = validationController;

    this.validationController.subscribe((event) => {
      if (event.type === 'reset') {
        this.warnings.weight.show = false;
      }

      if (event.type === 'validate') {
        if (!this.net.IsMorenotNet) {
          this.warnings.weight.show =
            this.net.NetDimension.Weight && (this.net.NetDimension.Weight < 100 || this.net.NetDimension.Weight > 20000);
        }
      }
    });
  }

  protected applyValidationRules() {
    // Only apply validation rules if the net is not a mørenot net
    if (this.net.IsMorenotNet) return;

    this.validationBuilder
      .with(this.net.NetDimension)
      .required('SideCount', () => {
        return this.net.NetDimension.NetShapeId != 9;
      })
      .min('SideCount', 4)
      .max('SideCount', 100)
      .required('DepthBottom')
      .min('DepthBottom', 0)
      .max('DepthBottom', 100)
      .required('DepthBottomCenter')
      .min('DepthBottomCenter', 0)
      .max('DepthBottomCenter', 100)
      .required('DepthBottomCenterExtraBottom', () => {
        return this.net.NetDimension.NetShapeId === 3 || this.net.NetDimension.NetShapeId === 7;
      })
      .min('DepthBottomCenterExtraBottom', 0)
      .max('DepthBottomCenterExtraBottom', 100)
      .required('Weight', () => {
        return this.is2021Standard;
      })
      .min('Weight', 0)
      .required('SideRopes')
      .min('SideRopes', 1)
      .required('CircumferenceProduction')
      .custom('CircumferenceProduction', 'validation.circumferenceOutOfRange', (value) => {
        const circumference = +value;

        if (!circumference) return true;
        if (!this.net.NetDimension?.Circumference) return true;

        const maxCircumference = this.net.NetDimension.Circumference * 1.1;
        const minCircumference = this.net.NetDimension.Circumference * 0.9;

        if (circumference > maxCircumference || circumference < minCircumference) {
          return false;
        }
        return true;
      })
      .required('BottomRopeCircumference', () => {
        return this.net.NetDimension.NetShapeId != 3 && this.net.NetDimension.NetShapeId != 7;
      })
      .on(this.net.NetDimension);
  }

  async activate(params: { NetId?: number; Id?: number }) {
    this.netId = params.NetId || params.Id;

    await this.getNet(this.netId);

    await Promise.all([this.getNetTypes(), this.getNetShapes(), this.getDesignTypes(), this.getDimensionClasses(), this.getNettingTypes()]);

    this.filterNetTypesBasedOnNetShape();

    // Get new tabIndex for tabs component via EA, store value so we can publish this if canDeactivate returns true
    this.eventAggregator.subscribe('changeTab', (tabIndex: number) => {
      this.nextTabIndex = tabIndex;
    });

    this.refreshDimensionsSubscription = this.eventAggregator.subscribe('refresh-calculated-dimensions', () => {
      this.getNetDimensionCalculated();
    });

    this.inInitialPhase = false;
  }

  created() {
    if (this.router.currentInstruction.config.name === 'offer-detail-dimension') {
      this.offerDrawingsVisible = true;
      this.otherDrawingsVisible = false;
    } else {
      this.offerDrawingsVisible = false;
      this.otherDrawingsVisible = true;
    }
  }

  protected getNettingTypeName(netting: Models.Netting) {
    if (netting.NettingTypeId) {
      return this.nettingtypes.filter((nettingType) => nettingType.Id === netting.NettingTypeId).map((nettingType) => nettingType.Name);
    } else {
      let nettingTypeName = '';
      if (netting.Material) {
        nettingTypeName += netting.Material.Name + ' ';
      }
      if (netting.ThreadType) {
        nettingTypeName += netting.ThreadType.Name + ' ';
      }
      if (netting.NylonType) {
        nettingTypeName += netting.NylonType.Name + ' ';
      }
      if (netting.MeshSize) {
        nettingTypeName += netting.MeshSize.Name;
      }
      return nettingTypeName;
    }
  }

  protected getSlopeAngleFormula() {
    const d = this.dimensionLabels;

    if (this.net?.NetDimension?.NetShapeId === NetShapeIds.Rectangular) {
      return this.formula([
        `${d.NettingLengthBottomLength} = Sqrt(((${d.BottomSquareLength} || ${d.SquareLength}) / 2)² + ${d.DepthBottomCenter}²) \n`,
        `${d.SlopeAngleBottom} = Asin(${d.DepthBottomCenter} / ${d.NettingLengthBottomLength}) * 180 / π`,
      ]);
    }

    return this.formula([
      `${d.RadiusBottomRope} = (${d.BottomRopeCircumference} / ${d.SideCount}) / (2 * tan(π / ${d.SideCount})) \n`,
      `${d.SlopeAngleBottom} = arctan(${d.DepthBottomCenter} / ${d.RadiusBottomRope}) * 180 / π`,
    ]);
  }

  protected getVolumeFormula() {
    const d = this.dimensionLabels;

    switch (this.net?.NetDimension?.NetShapeId) {
      case NetShapeIds.Rectangular:
        return this.formula([
          `${d.VolumeToBottomRope} = ((${d.AreaHorizontalBottomRope} + ${d.AreaHorizontalWaterLine}) / 2) * ${d.DepthBottom} \n`,
          `${d.VolumeInnerBottom} = ${d.BottomLength} * ${d.BottomWidth} * ${d.DepthBottomCenter} / 3 \n`,
          `${d.VolumeNet} = ${d.VolumeToBottomRope} + ${d.VolumeInnerBottom}`,
        ]);

      case NetShapeIds.Spaghetti:
        return this.formula([
          `${d.WaterlineSideLength} = ${d.Circumference} / ${d.SideCount} \n`,
          `${d.TotalDepth} = ${d.DepthBottom} + ${d.DepthBottomCenterExtraBottom} \n`,
          `${d.BottomSideLength} = ${d.WaterlineSideLength} * (${d.TotalDepth} - ${d.DepthBottom}) / ${d.TotalDepth} \n`,
          `${d.AreaWaterline} = ${d.SideCount} * ${d.WaterlineSideLength}² / (4 * tan(π / ${d.SideCount})) \n`,
          `${d.AreaBottom} = ${d.SideCount} * ${d.BottomSideLength}² / (4 * tan(π / ${d.SideCount})) \n`,
          `${d.VolumeToBottomRope} = ${d.DepthBottom} * (${d.AreaWaterline} + ${d.AreaBottom} + √(${d.AreaWaterline} * ${d.AreaBottom})) / 3 \n`,
          `${d.VolumeOuterBottom} = ${d.AreaBottom} × ${d.DepthBottomCenter} / 3 \n`,
          `${d.VolumeNet} = ${d.VolumeToBottomRope} + ${d.VolumeOuterBottom} \n`,
        ]);

      case NetShapeIds.Conednet:
      case NetShapeIds.Cylinder:
        return this.formula([
          `${d.WaterlineSideLength} = ${d.Circumference} / ${d.SideCount} \n`,
          `${d.BottomSideLength} = ${d.BottomRopeCircumference} / ${d.SideCount} \n`,
          `${d.AreaWaterline} = ${d.SideCount} * ${d.WaterlineSideLength}² / (4 * tan(π / ${d.SideCount})) \n`,
          `${d.AreaBottom} = ${d.SideCount} * ${d.BottomSideLength}² / (4 * tan(π / ${d.SideCount})) \n`,
          `${d.VolumeToBottomRope} = ${d.DepthBottom} * (${d.AreaWaterline} + ${d.AreaBottom} + Sqrt(${d.AreaWaterline} * ${d.AreaBottom})) / 3 \n`,
          `${d.VolumeOuterBottom} = ${d.AreaBottom} * ${d.DepthBottomCenter} / 3 \n`,
          `${d.VolumeNet} = ${d.VolumeToBottomRope} + ${d.VolumeOuterBottom} \n`,
          '',
          `${d.BottomCircumferenceGreaterThanMain} \n`,
          ...this.genericVolumeFormula(),
        ]);

      case NetShapeIds.Straightsides:
      default:
        return this.formula(this.genericVolumeFormula());
    }
  }

  protected getAreaNettingInSeaFormula() {
    const d = this.dimensionLabels;

    switch (this.net?.NetDimension?.NetShapeId) {
      case NetShapeIds.Rectangular:
        return this.formula([
          `${d.TopSquareLengthFull} = ${d.SquareLength} + ${d.SlopeCornerLength} * 2 \n`,
          `${d.DiffSidesTop} = (${d.TopSquareLengthFull} - ${d.BottomSquareLength}) / 2 \n`,
          `${d.SideLength} = Sqrt(${d.DiffSidesTop}² + ${d.DepthBottom}²) \n`,
          `${d.SideArea} = ((${d.SquareLength} + ${d.BottomSquareLength}) * ${d.SideLength}) + ((${d.SquareWidth} + ${d.BottomSquareWidth}) * ${d.SideLength}) \n`,
          `${d.TriangleArea} = ${d.SquareSlopeCornerLength} * ${d.DepthBottom} * 2 \n`,
          `${d.AreaWall} = ${d.SideArea} + ${d.TriangleArea} \n`,
          `${d.NettingBottomLength} = Sqrt((${d.BottomSquareLength} / 2)² + ${d.DepthBottomCenter}²) \n`,
          `${d.NettingBottomWidth} = Sqrt((${d.BottomSquareWidth} / 2)² + ${d.DepthBottomCenter}²) \n`,
          `${d.AreaInnerBottom} = (0.5 * ${d.NettingBottomLength} * ${d.BottomSquareWidth} + 0.5 * ${d.NettingBottomWidth} * ${d.BottomSquareLength}) * 2 \n`,
          '',
          ...this.areaNettingInSeaTotalFormula(),
        ]);

      case NetShapeIds.Spaghetti:
      case NetShapeIds.Conednet:
        return this.formula([
          `${d.WaterlineSideLength} = ${d.Circumference} / ${d.SideCount} \n`,
          `${d.BottomSideLength} = ${d.BottomRopeCircumference} / ${d.SideCount} \n`,
          `${d.AreaWall} = (${d.SideCount} / 4) * (${d.WaterlineSideLength} + ${d.BottomSideLength}) * Sqrt((cot(π / ${d.SideCount}) * (${d.BottomSideLength} - ${d.WaterlineSideLength}))² + (2 * ${d.DepthBottom})²) \n`,
          `${d.AreaInnerBottom} = (${d.BottomRopeCircumference} / 2) * ${d.RadiusMiddleSideToCenterBottomCone} \n`,
          ...this.areaNettingInSeaTotalFormula(),
        ]);

      case NetShapeIds.Cylinder:
      case NetShapeIds.Straightsides:
      default:
        if (this.net?.NetDimension?.IsCombiNet) {
          return this.formula([
            `${d.AreaWallCombiTopLargeFish} = ((${d.Circumference} + ${d.BottomRopeCircumference}) / 2) * (${d.DepthSecondMainRope} - ${d.HeightJumpNet}) \n`,
            `${d.AreaWallCombiBottomSmallFish} = ((${d.Circumference} + ${d.BottomRopeCircumference}) / 2) * (${d.HeightJumpNet} + ${d.DepthBottom} - ${d.DepthSecondMainRope}) \n`,
            `${d.AreaWall} = ${d.AreaWallCombiTopLargeFish} + ${d.AreaWallCombiBottomSmallFish} \n`,
            `${d.AreaInnerBottom} = (${d.BottomRopeCircumference} / 2) * ${d.RadiusMiddleSideToCenterBottomCone} \n`,
            ...this.areaNettingInSeaTotalFormula(),
          ]);
        }

        return this.formula([
          `${d.AreaWall} = ((${d.Circumference} + ${d.BottomRopeCircumference}) / 2) * ${d.DepthBottom}`,
          `${d.AreaInnerBottom} = (${d.BottomRopeCircumference} / 2) * ${d.RadiusMiddleSideToCenterBottomCone}`,
          '',
          ...this.areaNettingInSeaTotalFormula(),
        ]);
    }
  }

  protected getAreaWashableFormula() {
    const d = this.dimensionLabels;

    switch (this.net?.NetDimension?.NetShapeId) {
      case NetShapeIds.Rectangular:
        return this.formula([
          `${d.NettingBottomLength} = Sqrt(((${d.BottomSquareLength} + ${d.SquareSlopeCornerLength}) / 2)² + ${d.DepthBottomCenter}²) \n`,
          `${d.NettingBottomWidth} = Sqrt(((${d.BottomSquareWidth} + ${d.SquareSlopeCornerLength}) / 2)² + ${d.DepthBottomCenter}²) \n`,
          `${d.AreaSideABottom} = (${d.BottomSquareWidth} + ${d.SquareSlopeCornerLength}) * ${d.NettingBottomWidth} \n`,
          `${d.AreaSideBBottom} = (${d.BottomSquareLength} + ${d.SquareSlopeCornerLength}) * ${d.NettingBottomLength} \n`,
          `${d.AreaWashableTop} = (${d.SquareLength} + ${d.SquareWidth}) * 2 * ${d.DepthBottom} \n`,
          `${d.AreaWashableBottom} = ${d.AreaSideABottom} + ${d.AreaSideBBottom} \n`,
          `${d.AreaWashable} = ${d.AreaWashableTop} + ${d.AreaWashableBottom}`,
        ]);

      case NetShapeIds.Cylinder:
        return this.formula([
          `Radius = ${d.Circumference} / π / 2 \n`,
          `${d.AreaWashableTop} = ${d.DepthBottom} * ${d.Circumference} \n`,
          `${d.AreaWashableBottom} = π * Radius * Sqrt(Radius² + ${d.DepthBottomCenter}²) \n`,
          `${d.AreaWashable} = ${d.AreaWashableTop} + ${d.AreaWashableBottom}`,
        ]);

      case NetShapeIds.Conednet:
        return this.formula([
          `Radius = ${d.Circumference} / π / 2 \n`,
          `${d.AreaWashable} = π * Radius * Sqrt(Radius² + (${d.DepthBottom} + ${d.DepthBottomCenter})²)`,
        ]);

      case NetShapeIds.Spaghetti:
        return this.formula([
          `Radius = ${d.Circumference} / π / 2 \n`,
          `${d.RadiusBottomRope} = ${d.BottomRopeCircumference} / π / 2 \n`,
          `${d.AreaWashableTop} = π * (Radius + ${d.RadiusBottomRope}) * Sqrt(${d.DepthBottom}² + (Radius - ${d.RadiusBottomRope})²) \n`,
          `${d.AreaWashableBottom} = π * ${d.RadiusBottomRope} * Sqrt(${d.RadiusBottomRope}² + ${d.DepthBottomCenter}²) \n`,
          `${d.AreaWashable} = ${d.AreaWashableTop} + ${d.AreaWashableBottom}`,
        ]);

      case NetShapeIds.Straightsides:
      default:
        return this.formula([d.NoWashableAreaFormula]);
    }
  }

  protected getAreaNettingFormula() {
    const d = this.dimensionLabels;
    const lines = [
      `${d.ExcessNettingFactor} = ((100 + ${d.ExcessNettingPercentage}) / 100)² - 1 \n`,
      `${d.AreaJumpingNet} = ${d.HeightJumpNet} * ${d.Circumference} \n`,
      `${d.AreaReinforcementTotal} = ${d.AreaReinforcementBottom} + ${d.AreaReinforcementBottomRope} + ${d.AreaReinforcementExtraRope} + ${d.AreaReinforcementMainRope} \n`,
      `${d.AreaTotal} = ${d.AreaWall} + ${d.AreaInnerBottom} + ${d.AreaJumpingNet} + ${d.AreaReinforcementTotal} \n`,
    ];

    if (this.net?.NetDimension?.NetShapeId === NetShapeIds.Conednet) {
      lines.push(`${d.AreaTotal} ${d.Includes} ${d.AreaOuterBottom} \n`);
    }

    lines.push(`${d.AreaTotalExcess} = ${d.AreaTotal} * ${d.ExcessNettingFactor} \n`, `${d.AreaNetting} = ${d.AreaTotal} + ${d.AreaTotalExcess}`);

    return this.formula(lines);
  }

  private genericVolumeFormula() {
    const d = this.dimensionLabels;
    const lines = [
      `${d.VolumeToBottomRope} = ${d.DepthBottom} * (${d.AreaHorizontalWaterLine} + Sqrt(${d.AreaHorizontalWaterLine} * ${d.AreaHorizontalBottomRope}) + ${d.AreaHorizontalBottomRope}) / 3 \n`,
      `${d.VolumeInnerBottom} = ${d.DepthBottomCenter} / 3 * ${d.AreaHorizontalBottomRope}\n`,
    ];

    if (this.net?.NetDimension?.NetShapeId === NetShapeIds.Conednet) {
      lines.push(`${d.VolumeNet} = ${d.VolumeToBottomRope} + ${d.VolumeOuterBottom}`);
    } else {
      lines.push(`${d.VolumeNet} = ${d.VolumeToBottomRope} + ${d.VolumeInnerBottom}`);
    }

    return lines;
  }

  private areaNettingInSeaTotalFormula() {
    const d = this.dimensionLabels;
    return [
      `${d.ExcessNettingFactor} = ((100 + ${d.ExcessNettingPercentage}) / 100)² - 1 \n`,
      `${d.AreaInnerNetInSea} = ${d.AreaWall} + ${d.AreaInnerBottom} \n`,
      `${d.AreaInnerNetInSeaExcess} = ${d.AreaInnerNetInSea} * ${d.ExcessNettingFactor} \n`,
      `${d.AreaNetInSea} = ${d.AreaInnerNetInSea} + ${d.AreaInnerNetInSeaExcess}`,
    ];
  }

  private formula(lines: string[]) {
    return lines.join('\n');
  }

  private translate(key: string) {
    const translated = this.t.tr(key);
    return translated;
  }

  private get dimensionLabels() {
    return {
      AreaInnerBottom: this.translate('netsummary.area.AreaInnerBottom'),
      AreaJumpingNet: this.translate('netsummary.area.JumpingNet'),
      AreaNetInSea: this.translate('netdimension.AreaNetInSea'),
      AreaNetting: this.translate('netdimension.AreaNettingInNet'),
      AreaOuterBottom: this.translate('netsummary.area.AreaOuterBottom'),
      AreaReinforcementBottom: this.translate('netsummary.area.AreaReinforcementBottom'),
      AreaReinforcementBottomRope: this.translate('netsummary.area.AreaReinforcementBottomRope'),
      AreaReinforcementExtraRope: this.translate('netsummary.area.AreaReinforcementExtraRope'),
      AreaReinforcementMainRope: this.translate('netsummary.area.AreaReinforcementMainRope'),
      AreaReinforcementTotal: this.translate('netsummary.area.AreaReinforcementTotal'),
      AreaTotal: this.translate('netsummary.area.AreaTotal'),
      AreaTotalExcess: this.translate('netsummary.area.AreaTotalExcess'),
      AreaWall: this.translate('netsummary.area.AreaWall'),
      AreaWashable: this.translate('general.rinsArea'),
      BottomLength: this.translate('netdimension.BottomLength'),
      BottomRopeCircumference: this.translate('netdimension.BottomRopeCircumference'),
      BottomSquareLength: this.translate('netsummary.BottomSquareLength'),
      BottomSquareWidth: this.translate('netsummary.BottomSquareWidth'),
      BottomWidth: this.translate('netdimension.BottomWidth'),
      Circumference: this.translate('netdimension.CircumferenceMainRope'),
      DepthBottom: this.translate('netdimension.DepthBottom'),
      DepthBottomCenter: this.translate('netdimension.DepthBottomCenter'),
      DepthBottomCenterExtraBottom: this.translate('netdimension.DepthBottomCenterExtraBottom'),
      ExcessNettingPercentage: this.translate('netdimension.ExcessNettingPercentage'),
      HeightJumpNet: this.translate('netdimension.HeightJumpNet'),
      RadiusMiddleSideToCenterBottomCone: this.translate('netsummary.RadiusMiddleSideToCenterBottomCone'),
      SideCount: this.translate('netdimension.SideCount'),
      SlopeAngleBottom: this.translate('netdimension.SlopeAngleBottom'),
      SlopeCornerLength: this.translate('netdimension.SlopeCornerLength'),
      SquareLength: this.translate('netsummary.SquareLength'),
      SquareSlopeCornerLength: this.translate('netsummary.SquareSlopeCornerLength'),
      SquareWidth: this.translate('netsummary.SquareWidth'),
      VolumeNet: this.translate('netdimension.VolumeNet'),
      
      NettingLengthBottomLength: this.translate('netsummary.NettingLengthBottomLength'),
      RadiusBottomRope: this.translate('netsummary.RadiusBottomRope'),
      VolumeToBottomRope: this.translate('netsummary.VolumeToBottomRope'),
      VolumeInnerBottom: this.translate('netsummary.VolumeInnerBottom'),
      AreaHorizontalBottomRope: this.translate('netsummary.AreaHorizontalBottomRope'),
      AreaHorizontalWaterLine: this.translate('netsummary.AreaHorizontalWaterLine'),
      WaterlineSideLength: this.translate('netsummary.WaterlineSideLength'),
      TotalDepth: this.translate('netsummary.TotalDepthh'),
      BottomSideLength: this.translate('netsummary.BottomSideLength'),
      AreaWaterline: this.translate('netsummary.AreaWaterline'),
      AreaBottom: this.translate('netsummary.AreaBottom'),
      VolumeOuterBottom: this.translate('netsummary.VolumeOuterBottom'),
      BottomCircumferenceGreaterThanMain: this.translate('netsummary.BottomCircumferenceGreaterThanMain'),

      TopSquareLengthFull: this.translate('netsummary.TopSquareLengthFull'),
      DiffSidesTop: this.translate('netsummary.DiffSidesTop'),
      SideLength: this.translate('netsummary.SideLength'),
      SideArea: this.translate('netsummary.SideArea'),
      TriangleArea: this.translate('netsummary.TriangleArea'),
      NettingBottomLength: this.translate('netsummary.NettingBottomLength'),
      NettingBottomWidth: this.translate('netsummary.NettingBottomWidth'),
      ExcessNettingFactor: this.translate('netsummary.ExcessNettingFactor'),
      AreaInnerNetInSea: this.translate('netsummary.AreaInnerNetInSea'),
      AreaInnerNetInSeaExcess: this.translate('netsummary.AreaInnerNetInSeaExcess'),

      AreaWallCombiTopLargeFish: this.translate('netsummary.AreaWallCombiTopLargeFish'),
      AreaWallCombiBottomSmallFish: this.translate('netsummary.AreaWallCombiBottomSmallFish'),
      DepthSecondMainRope: this.translate('netdimension.DepthSecondMainRope'),
      Includes: this.translate('netsummary.Includes'),

      AreaSideABottom: this.translate('netsummary.AreaSideABottom'),
      AreaSideBBottom: this.translate('netsummary.AreaSideBBottom'),
      AreaWashableTop: this.translate('netsummary.AreaWashableTop'),
      AreaWashableBottom: this.translate('netsummary.AreaWashableBottom'),
      NoWashableAreaFormula: this.translate('netsummary.NoWashableAreaFormula'),
    };
  }

  setNetStandard(standardId: number) {
    if (!this.net || this.net.NetDimension?.NetStandardId == standardId) {
      return;
    }

    this.net.NetDimension.NetStandardId = standardId ? +standardId : null;

    void this.setNetStandardFlag();
    this.getNetDimensionCalculated();
  }

  // Get net and netdimension
  private async getNet(id: number) {
    const res = await this.netService.get(id + '?$expand=NetDimension');
    this.net = res;
    this.locked = res.Locked || this.netIsLinkedToAnalysis(res.NetDimension) ? true : false;

    if (this.net.NetDimension.DimensionClassId && this.net.IsMorenotNet) {
      if (this.net.NetDimension.DimensionClassId === 1) {
        this.dimensionclassType = 0;
      } else if (this.net.NetDimension.DimensionClassId === 10) {
        this.dimensionclassType = 1;
      } else if (this.net.NetDimension.DimensionClassId > 1 && this.net.NetDimension.DimensionClassId < 9) {
        this.dimensionclassType = 2;
      }
    }

    if (this.net.NetDimension.DimensionClassId && !this.net.IsMorenotNet) {
      if (this.net.NetDimension.DimensionClassId === 15) {
        this.dimensionclassTypeUsedNet = 15;
      } else if (this.net.NetDimension.DimensionClassId === 12) {
        this.dimensionclassTypeUsedNet = 12;
      } else if (this.net.NetDimension.DimensionClassId === 13) {
        this.dimensionclassTypeUsedNet = 13;
      } else if (this.net.NetDimension.DimensionClassId === 14) {
        this.dimensionclassTypeUsedNet = 14;
      }
    }

    if (this.net.NetDimension.NetShapeId) {
      this.currentNetShape = (await this.netShapeService.getAllCached()).find((x) => x.Id == this.net.NetDimension.NetShapeId);
    }

    this.getNetDimensionCalculated();
    this.updateOriginalObject();
    this.applyValidationRules();
    void this.setNetStandardFlag();

    this.nettings = await this.nettingService.getAll('?$filter=NetId eq ' + id + '&$expand=MeshSize,NylonType,ThreadType,Material');
  }

  protected netIsLinkedToAnalysis(netDimension: Models.NetDimension) {
    if (!netDimension) return false;
    return (
      !!netDimension.AnalysisVariantId ||
      !!netDimension.AnalysisVariant2Id ||
      !!netDimension.AnalysisVariant3Id ||
      !!netDimension.AnalysisVariant4Id ||
      !!netDimension.AnalysisVariant_2Id ||
      !!netDimension.AnalysisVariant2_2Id ||
      !!netDimension.AnalysisVariant3_2Id ||
      !!netDimension.AnalysisVariant4_2Id
    );
  }

  private getNetDimensionCalculated() {
    this.netDimensionService
      .calculateValues(this.net.Id, this.net.NetDimension)
      .then((res) => {
        this.netdimensionCalculated = res;
        if (!this.locked && this.net.IsMorenotNet) {
          if (this.netdimensionCalculated?.NetStandard?.Version === Models.NetStandardVersion.NS9415_2021) {
            this.net.NetDimension.DimensionClassId = this.netdimensionCalculated.CalculatedDimensionClassId;
          } else {
            if (this.net.NetDimension.DimensionClassId > 11) {
              this.setDimensionClassId(this.netdimensionCalculated.CalculatedDimensionClassId);
            }

            if (
              !this.net.NetDimension.OverrideDimensionClass &&
              this.net.NetDimension.DimensionClassId !== this.netdimensionCalculated.CalculatedDimensionClassId
            ) {
              this.net.NetDimension.DimensionClassId = this.netdimensionCalculated.CalculatedDimensionClassId;

              if (this.netdimensionCalculated.CalculatedDimensionClassId === 1) {
                this.dimensionclassType = 0;
              }

              if (this.netdimensionCalculated.CalculatedDimensionClassId > 1 && this.netdimensionCalculated.CalculatedDimensionClassId < 9) {
                this.dimensionclassType = 2;
              }
            }
          }

          // for coned/spagetti nets, calculate the bottom rope circumference
          if (
            this.net.NetDimension.NetShapeId === 3 ||
            this.net.NetDimension.NetShapeId === 7 ||
            (this.net.NetDimension.NetShapeId == 9 && this.net.IsMorenotNet)
          ) {
            this.net.NetDimension.BottomRopeCircumference = this.netdimensionCalculated.BottomRopeCircumference;
          }
          this.classType = dimensionClass[this.netdimensionCalculated.CalculatedDimensionClassId];
        }
      })
      .catch((err) => this.errorService.handleError(err));
  }

  private updateNet() {
    const netModification = new Models.Net();

    let changed = false;

    if (this.originalObject.CustomerNetTypeId !== this.net.CustomerNetTypeId) {
      netModification.CustomerNetTypeId = this.net.CustomerNetTypeId;
      changed = true;
    }

    if (this.originalObject.DrawingFileId !== this.net.DrawingFileId) {
      netModification.DrawingFileId = this.net.DrawingFileId;
      changed = true;
    }

    if (this.originalObject.ProductionDrawingFileId !== this.net.ProductionDrawingFileId) {
      netModification.ProductionDrawingFileId = this.net.ProductionDrawingFileId;
      changed = true;
    }

    if (this.originalObject.CustomerDrawingFileId !== this.net.CustomerDrawingFileId) {
      netModification.CustomerDrawingFileId = this.net.CustomerDrawingFileId;
      changed = true;
    }

    if (changed) {
      netModification.Id = this.net.Id;

      this.netService
        .put(netModification, netModification.Id)
        .then((result) => {
          this.eventAggregator.publish('offer-refresh', 'offer');

          this.toastService.showSuccess('net.updated');
        })
        .catch((err) => this.errorService.handleError(err));
    }
  }

  private async updateNetDimension() {
    const { valid } = await this.validationController.validate();
    if (!valid) {
      if (
        (this.net.NetDimension.NetStandardId === Number(Models.NetStandardId.NS9415_2009) ||
          this.net.NetDimension.NetStandardId === Number(Models.NetStandardId.NS9415_2021)) &&
        this.net.NetDimension.DimensionClassId === null
      ) {
        this.isNS9415 = true;
      }
      this.toastService.showWarning('general.checkValidationErrors');
      return;
    }

    if (
      (this.net.NetDimension.NetStandardId === Number(Models.NetStandardId.NS9415_2009) ||
        this.net.NetDimension.NetStandardId === Number(Models.NetStandardId.NS9415_2021)) &&
      this.net.NetDimension.DimensionClassId === null
    ) {
      this.toastService.showWarning('general.checkValidationErrors');
      this.isNS9415 = true;
      return;
    } else {
      this.isNS9415 = false;
    }

    // weight is always calculated server side for none mørenot nets, so dont save this information
    if (this.net.IsMorenotNet) {
      delete this.net.NetDimension.Weight;
    }

    this.netDimensionService
      .put(this.net.NetDimension, this.net.NetDimension.Id)
      .then((res) => {
        // Also update net if DesignType has changed
        if (
          this.originalObject.CustomerNetTypeId !== this.net.CustomerNetTypeId ||
          this.originalObject.DrawingFileId !== this.net.DrawingFileId ||
          this.originalObject.ProductionDrawingFileId !== this.net.ProductionDrawingFileId ||
          this.originalObject.CustomerDrawingFileId !== this.net.CustomerDrawingFileId
        ) {
          const netCopy = JSON.parse(JSON.stringify(this.net));
          netCopy.NetDimension = null;
          netCopy.Nettings = null;
          this.netService
            .put(netCopy, netCopy.Id)
            .then((result) => {
              this.toastService.showSuccess('net.updated');
            })
            .catch((err) => this.errorService.handleError(err));
        }

        this.updateOriginalObject();
        this.toastService.showSuccess('netdimension.updated');

        this.eventAggregator.publish('offer-refresh', 'price');
        this.eventAggregator.publish('offer-refresh', 'offer');
        this.eventAggregator.publish('netdimension-updated');
      })
      .catch((err) => this.errorService.handleError(err));
  }

  private updateOriginalObject() {
    if (!this.net) {
      return;
    }
    this.originalObject = JSON.parse(JSON.stringify(this.net));
  }

  async attached() {}

  private detached() {
    if (this.refreshDimensionsSubscription) {
      this.refreshDimensionsSubscription.dispose();
    }
  }

  private deactivate() {
    if (this.refreshDimensionsSubscription) {
      this.refreshDimensionsSubscription.dispose();
    }
  }

  protected async setNetShape(netShapeId: number) {
    this.net.NetDimension.NetShapeId = netShapeId;

    if (netShapeId) {
      this.currentNetShape = (await this.netShapeService.getAllCached()).find((x) => x.Id == this.net.NetDimension.NetShapeId);

      if (!this.currentNetShape?.CanUseCombiNet) {
        this.net.NetDimension.IsCombiNet = false;
      }
    }

    this.filterNetTypesBasedOnNetShape();
    this.getNetDimensionCalculated();
  }

  private setTableNetUsedNet(dimensionClassId: number) {
    if (this.net && this.net.NetDimension) {
      this.dimensionclassTypeUsedNet = dimensionClassId;
      this.net.NetDimension.DimensionClassId = dimensionClassId;
      this.net.NetDimension.OverrideDimensionClass = false;
    }
    return true;
  }

  private setTableNet() {
    this.dimensionclassTypeChanged(2, this.dimensionclassType);
    return true;
  }

  private dimensionclassTypeChanged(newValue, oldValue) {
    if (newValue?.toString() === '2') {
      // tabellnot - kun lov hvis kalkulert DimensionClassId er 2-8 (klasse 1-7)
      if (this.netdimensionCalculated) {
        if (this.net && this.net.NetDimension) {
          this.dimensionclassType = 2;
          this.net.NetDimension.Hs = +'2.5';
          this.net.NetDimension.Vs = +'0.75';
          this.net.NetDimension.DimensionClassId = this.netdimensionCalculated.CalculatedDimensionClassId;
          this.net.NetDimension.OverrideDimensionClass = false;
        }
      }
    }
  }

  async setNetStandardFlag() {
    const standards = await this.netStandardService.getAllCached();
    const standard = standards.find((s) => +s.Id === +this.net.NetDimension.NetStandardId);
    this.is2021Standard = standard?.Version === Models.NetStandardVersion.NS9415_2021;
    if (!this.inInitialPhase) {
      this.dimensionclassType = null;
      this.dimensionclassTypeUsedNet = null;
      this.net.NetDimension.Hs = null;
      this.net.NetDimension.Vs = null;
      this.net.NetDimension.DimensionClassId = null;
    }
    if (this.net.IsMorenotNet && this.is2021Standard) this.dimensionclassType = 0;
    this.eventAggregator.publish('net-standard-changed', standard);
  }

  async getNetStandards() {
    if (!this.net.IsMorenotNet) {
      return await this.netStandardService.getAll('?$filter=Id ne ' + Models.NetStandardId.NS9415_FaroeIslands);
    } else {
      //NetStandard RES 1821 (Chile) is temporary only visible and used in used net so hide here
      //NetStandard NS9415 Faroe Islands is only visible on service so hide here
      return await this.netStandardService.getAll('?$filter=Id ne ' + Models.NetStandardId.RES_1821 + ' and Id ne ' + Models.NetStandardId.NS9415_FaroeIslands);
    }
  }

  private setDimensionClassId(dimensionClassId: number) {
    this.net.NetDimension.DimensionClassId = dimensionClassId;
    this.net.NetDimension.OverrideDimensionClass = true;

    if (this.net.NetDimension.DimensionClassId === 1) {
      this.dimensionclassType = 0;
    } else if (this.net.NetDimension.DimensionClassId === 10) {
      this.dimensionclassType = 1;
    } else if (this.net.NetDimension.DimensionClassId > 1 && this.net.NetDimension.DimensionClassId < 9) {
      this.dimensionclassType = 2;
    }

    return true;
  }

  private setDrawingFileId(event: any) {
    this.net.DrawingFileId = event.detail.id;
    this.fileUploaded = true;
    this.updateNet();
    logger.debug('fileUploaded.');
  }

  private setCustomerDrawingFileId(event: any) {
    this.net.CustomerDrawingFileId = event.detail.id;
    this.fileUploaded = true;
    this.updateNet();
    logger.debug('fileUploaded..');
  }

  private setProductionDrawingFileId(event: any) {
    this.net.ProductionDrawingFileId = event.detail.id;
    this.fileUploaded = true;
    this.updateNet();
    logger.debug('fileUploaded...');
  }

  private removeDrawingFileId() {
    this.net.DrawingFileId = null;
    this.updateNet();
  }

  private removeCustomerDrawingFileId() {
    this.net.CustomerDrawingFileId = null;
    this.updateNet();
  }

  private removeProductionDrawingFileId() {
    this.net.ProductionDrawingFileId = null;
    this.updateNet();
  }

  private cancel() {
    if (this.router.baseUrl.includes('offer')) {
      this.router.navigateToRoute('offer-list');
    }
    if (this.router.baseUrl.includes('order')) {
      this.router.navigateToRoute('order-list');
    }
    if (this.router.baseUrl.includes('net-detail')) {
      this.router.navigateToRoute('net-list');
    }
  }

  private canDeactivate() {
    if (this.originalObject && !this.utility.areEqual(this.net, this.originalObject) && !this.locked) {
      // tslint:disable-next-line:no-console
      return this.dialogService
        .open({
          viewModel: Prompt,
          model: {
            header: 'dialog.pleaseConfirmHeader',
            message: 'dialog.unsavedChangesText',
          },
        })
        .whenClosed((response) => {
          if (response.wasCancelled) {
            return false;
          } else {
            const result = response.output;
            if (result === 'save') {
              // save the nettingtype and let that function handle the rest of the logic
              void this.updateNetDimension();
              return false;
            } else {
              this.eventAggregator.publish('changeTab-success', this.nextTabIndex);
              return true;
            }
          }
        });
    } else {
      this.eventAggregator.publish('changeTab-success', this.nextTabIndex);
      return true;
    }
  }

  protected lookup(slot: number | undefined, part: number) {
    this.dialogService
      .open({
        viewModel: AnalysisLookupDialog,
        model: {
          netId: this.net?.Id,
          slot,
        },
        lock: false,
        position: () => {
          //
        },
      })
      .whenClosed(async (response: DialogCloseResult) => {
        if (response.wasCancelled) return false;
        const analysis = response.output;

        try {
          await this.netDimensionService.linkAnalysis(this.net.NetDimensionId, {
            Slot: slot,
            AnalysisId: analysis.AnalysisId,
            Part: part,
            AnalysisVariantId: analysis.AnalysisVariantId,
          });
          await this.getNet(this.net.Id);
        } catch (error) {
          this.errorService.handleError(error);
        }

        document.querySelector('html').style.overflowY = null;
      })
      .catch((err) => {
        document.querySelector('html').style.overflowY = null;
        this.errorService.handleError(err);
      });
  }

  protected async removeAnalysis(slot: number, part: number) {
    try {
      await this.netDimensionService.unLinkAnalysis(this.net.NetDimensionId, {
        Slot: slot,
        Part: part,
      });
      await this.getNet(this.net.Id);
    } catch (error) {
      this.errorService.handleError(error);
    }
  }

  protected async onIsCollectChanged(isCollect: boolean) {
    this.net.IsCollect = isCollect;
    try {
      await this.netService.put(
        {
          IsCollect: isCollect,
        } as Models.Net,
        this.net.Id
      );
      this.toastService.showSuccess('net.updated');
      await this.getNet(this.net.Id);
    } catch (error) {
      this.errorService.handleError(error);
    }
  }

  protected setNetType(netTypeId: number | undefined) {
    this.net.NetDimension.NetTypeId = netTypeId;
    const type = this.netTypes.find((x) => x.Id == netTypeId);

    const circumference = type?.Circumference || 0;
    this.net.NetDimension.Circumference = circumference;
    this.net.NetDimension.CircumferenceProduction = circumference;
  }

  protected setNetDesign(designTypeId: number | undefined) {
    this.net.NetDimension.DesignTypeId = designTypeId;
  }

  protected filterNetTypesBasedOnNetShape() {
    if (!this.net?.NetDimension || !this.net.NetDimension.NetShapeId) return;
    const isSquaredNet = this.net.NetDimension.NetShapeId == 9;

    this.netTypesFiltered = [];

    setTimeout(() => {
      this.netTypesFiltered = this.netTypes
        .filter((x) => {
          return isSquaredNet ? x.Name.includes('x') : !x.Name.includes('x');
        })
        .filter((x) => FilterDeletedAndInactive(x));
    }, 30);
  }

  @AsyncErrorHandler
  protected async getNetTypes() {
    this.netTypes = await this.netTypeService.getAllCached();
    this.netTypesFiltered = this.netTypes;
    return this.netTypes;
  }

  @AsyncErrorHandler
  protected async getDesignTypes() {
    const res = await this.designTypeService.getAllCached();
    this.designTypes = res.filter((v) => FilterDeletedAndInactive(v));
  }

  @AsyncErrorHandler
  protected async getNetShapes() {
    const res = await this.netShapeService.getAllCached();
    this.netShapes = res.filter((v) => FilterDeletedAndInactive(v));
  }

  @AsyncErrorHandler
  protected async getNettingTypes() {
    const res = await this.nettingTypeService.getAllCached();
    this.nettingtypes = res.filter((v) => FilterDeletedAndInactive(v));
  }

  @AsyncErrorHandler
  protected async getDimensionClasses() {
    const classes = await this.dimensionClassService.getAllCached();
    this.dimensionclasses = classes.filter((obj) => obj.Id > 1 && obj.Id < 9);
  }
}
